import { Sparkles, Loader2, RefreshCcw, MessageSquare, FileText, ListTodo, Users, AlertCircle, AlertTriangle, Lightbulb, Brain, Target, TrendingUp, CalendarDays, Download } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AppLayout from '../components/AppLayout';
import QualitativeSentimentPanel from '@/components/dashboard/QualitativeSentimentPanel';
import { getGrowthDashboard, refreshGrowthDashboard } from '../lib/api';
import { useUsageSummary } from '../hooks/useUsageSummary';
import { exportUserData } from '../lib/exportData';

const sentimentColor = (score: number) => {
  if (score >= 0.25) return 'text-green-600 bg-green-50';
  if (score <= -0.15) return 'text-red-600 bg-red-50';
  return 'text-slate-600 bg-slate-50';
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (err) {
    return value;
  }
};

const formatRelative = (value: string | null | undefined) => {
  if (!value) return 'No recent refresh';
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  if (Number.isNaN(diffMs)) return formatDateTime(value);
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return formatDateTime(value);
};

const SHOW_NUMERIC_SENTIMENT = false;
const SHOW_QUALITATIVE_SENTIMENT = true;

const InsightsPage = () => {
  const { getToken, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const { data: usageData } = useUsageSummary();

  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['growth-dashboard'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return getGrowthDashboard(token);
    },
    enabled: isSignedIn,
    refetchOnMount: true,
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return refreshGrowthDashboard(token);
    },
    onSuccess: (payload) => {
      queryClient.setQueryData(['growth-dashboard'], payload);
    },
    onError: (error) => {
      if (error?.message?.includes('daily_limit_exceeded')) {
        // Show error message for rate limit
        alert('You can refresh insights up to twice every 24 hours. Please try again later.');
      } else {
        alert('Failed to refresh insights. Please try again.');
      }
    },
  });

  if (isLoading) {
    return (
      <AppLayout currentPage="insights-hub">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <Loader2 className="h-10 w-10 animate-spin text-royal-blue mx-auto" />
            <p className="text-slate-600 font-display">Analyzing your journey…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (isError || !data) {
    return (
      <AppLayout currentPage="insights-hub">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-red-100 shadow-sm text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto" strokeWidth={1.5} />
            <h2 className="text-xl font-display font-bold text-slate-900">Unable to load insights</h2>
            <p className="text-slate-600">{(error as Error)?.message || 'Something went wrong while fetching your analytics.'}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const metrics = data.journey_metrics;
  const coachMixEntries = Object.entries(data.coach_engagement_mix?.data || {});
  const sentimentTrend = data.sentiment_trend?.data || [];
  const blindspots = data.blindspot_alerts?.data || [];
  const reflective = data.reflective_questions?.data || [];
  const mentalModels = data.mental_models?.data || [];
  const highlights = data.mom_highlights || [];
  const timeline = data.session_timeline || [];

  const handleRefresh = () => {
    if (!refreshMutation.isPending) {
      refreshMutation.mutate();
    }
  };

  const handleExportAnalytics = async () => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      await exportUserData(token);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export analytics data. Please try again.');
    }
  };

  const refreshLabel = refreshMutation.isPending ? 'Refreshing…' : 'Refresh from latest MoMs';

  return (
    <AppLayout currentPage="insights-hub">
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <section className="rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-royal-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-royal-blue">
                  <Sparkles className="h-3.5 w-3.5" />
                  MoM-Powered Insights
                </div>
                <h1 className="font-display text-3xl font-semibold text-slate-900 md:text-4xl">
                  Coaching Journey Insights
                </h1>
                <p className="text-base text-slate-600 max-w-2xl">
                  Your Minutes of Meeting are distilled into blindspots, reflective nudges, and growth metrics—so you can spot hidden patterns faster.
                </p>
              </div>
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
                  Source: {data.analysis_source === 'mom_summaries' ? 'MoM summaries' : data.analysis_source === 'conversation_messages' ? 'Chat transcripts' : 'Unknown'}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleExportAnalytics}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-display font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    title="Download analytics data for analysis"
                  >
                    <Download className="h-4 w-4" />
                    Export Data
                  </button>
                  <div className="relative group">
                    <button
                      type="button"
                      onClick={handleRefresh}
                      disabled={refreshMutation.isPending || isFetching}
                      className="inline-flex items-center gap-2 rounded-2xl bg-royal-blue px-4 py-2 text-sm font-display font-semibold !text-white shadow-sm transition hover:bg-royal-blue/90 disabled:opacity-60"
                      title="You can refresh insights up to twice every 24 hours"
                    >
                    {refreshMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCcw className="h-4 w-4" />
                    )}
                    {refreshLabel}
                    </button>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 hidden group-hover:block">
                      <div className="bg-slate-900 text-white text-xs rounded-lg py-2 px-3 text-center">
                        You can refresh insights up to twice every 24 hours
                        <div className="absolute bottom-[-5px] left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <span>Last refreshed: {formatRelative(data.last_insights_refresh)}</span>
              <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline-block" aria-hidden />
              <span>Total highlights: {highlights.length}</span>
            </div>
          </section>

          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[{
              title: 'Coaching Sessions',
              value: metrics.totalSessions,
              icon: MessageSquare,
              tone: 'bg-blue-50 text-blue-600',
              description: 'Total conversations logged',
            }, {
              title: 'MoM Coverage',
              value: `${metrics.sessionsWithMoM}/${metrics.totalSessions || 1}`,
              icon: FileText,
              tone: 'bg-purple-50 text-purple-600',
              description: 'Sessions captured as MoMs',
            }, {
              title: 'Total Chats',
              value: metrics.totalChats,
              icon: ListTodo,
              tone: 'bg-amber-50 text-amber-600',
              description: 'Messages across all sessions',
            }, {
              title: 'Action Items',
              value: metrics.totalActionItems,
              icon: Users,
              tone: 'bg-emerald-50 text-emerald-600',
              description: 'MoM-generated next steps',
            }].map((metric) => (
              <div key={metric.title} className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${metric.tone}`}>
                    <metric.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{metric.title}</p>
                    <p className="text-2xl font-semibold text-slate-900">{metric.value}</p>
                    <p className="text-xs text-slate-500">{metric.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">Recent Aha! Moments</h2>
                <Sparkles className="h-5 w-5 text-royal-blue" />
              </div>
              <p className="mt-2 text-sm text-slate-600">Highlights surfaced directly from your latest MoMs.</p>
              <div className="mt-4 space-y-4">
                {highlights.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
                    Generate a MoM to start capturing breakthrough moments.
                  </div>
                )}
                {highlights.map((highlight) => (
                  <div key={highlight.session_id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-lg font-semibold text-slate-900">{highlight.title}</h3>
                      <span className="text-xs text-slate-500">{formatDateTime(highlight.occurred_at)}</span>
                    </div>
                    <ul className="mt-3 space-y-2">
                      {highlight.key_takeaways.slice(0, 3).map((takeaway, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-royal-blue" />
                          <span>{takeaway}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">Journey Timeline</h2>
                <CalendarDays className="h-5 w-5 text-royal-blue" />
              </div>
              <p className="mt-2 text-sm text-slate-600">Six most recent sessions and what stood out.</p>
              <div className="mt-4 space-y-4">
                {timeline.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
                    Start a coaching session to build your timeline.
                  </div>
                ) : (
                  timeline.map((item) => (
                    <div key={item.session_id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs uppercase text-slate-500">{item.coach_type || 'coach'}</p>
                          <h3 className="font-display text-base font-semibold text-slate-900">{item.title}</h3>
                        </div>
                        <span className="text-xs text-slate-500">{formatDateTime(item.occurred_at)}</span>
                      </div>
                      {item.summary && (
                        <p className="mt-2 text-sm text-slate-600">{item.summary}</p>
                      )}
                      {item.key_takeaways.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {item.key_takeaways.slice(0, 2).map((point, idx) => (
                            <li key={idx} className="text-sm text-slate-700">
                              • {point}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">Blindspot Alerts</h2>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <p className="mt-2 text-sm text-slate-600">Signals that may be holding you back.</p>
              <div className="mt-4 space-y-3">
                {blindspots.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/70 p-6 text-sm text-amber-700">
                    No blindspots detected yet. Revisit your action items after the next session.
                  </div>
                ) : (
                  blindspots.map((spot, idx) => (
                    <div key={`${spot.type}-${idx}`} className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">{spot.type}</p>
                      <p className="mt-2 text-sm text-amber-900">{spot.alert}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">Reflective Nudges</h2>
                <Lightbulb className="h-5 w-5 text-royal-blue" />
              </div>
              <p className="mt-2 text-sm text-slate-600">Questions curated to unlock your next aha moment.</p>
              <div className="mt-4 space-y-4">
                {reflective.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
                    Generate a MoM to unlock tailored reflective questions.
                  </div>
                ) : (
                  reflective.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-sm font-semibold text-slate-900">{item.prompt}</p>
                      {item.why_it_matters && (
                        <p className="mt-2 text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">Why it matters:</span> {item.why_it_matters}
                        </p>
                      )}
                      {item.micro_action && (
                        <p className="mt-2 text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">Try this:</span> {item.micro_action}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">Mental Models to Apply</h2>
                <Brain className="h-5 w-5 text-violet-500" />
              </div>
              <p className="mt-2 text-sm text-slate-600">Frameworks tied directly to your recent themes.</p>
              <div className="mt-4 space-y-4">
                {mentalModels.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-violet-200 bg-violet-50/70 p-6 text-sm text-violet-700">
                    No mental models surfaced yet. Generate a new MoM to unlock fresh perspectives.
                  </div>
                ) : (
                  mentalModels.map((model, idx) => (
                    <div key={idx} className="rounded-2xl border border-violet-200 bg-white p-4 shadow-sm">
                      <p className="text-sm font-semibold text-slate-900">{model.name}</p>
                      <p className="mt-2 text-xs text-slate-600">{model.summary}</p>
                      <p className="mt-2 text-xs text-slate-600">
                        <span className="font-semibold text-slate-700">How to apply:</span> {model.how_to_apply}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">Coach Engagement Mix</h2>
                <Target className="h-5 w-5 text-slate-500" />
              </div>
              <p className="mt-2 text-sm text-slate-600">Where you’re spending most coaching time.</p>
              <div className="mt-4 space-y-3">
                {coachMixEntries.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
                    We haven’t logged any sessions with specific coaches yet.
                  </div>
                ) : (
                  coachMixEntries.map(([coach, count], idx) => (
                    <div key={coach} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm text-slate-700">
                        <span className="font-medium text-slate-900">{coach}</span>
                        <span>{count} sessions</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100">
                        <div
                          className={`h-full rounded-full ${idx % 2 === 0 ? 'bg-royal-blue' : 'bg-emerald-500'}`}
                          style={{ width: `${Math.min(100, (Number(count) / Math.max(1, metrics.totalSessions)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          {SHOW_QUALITATIVE_SENTIMENT && (
            <QualitativeSentimentPanel timeline={timeline} />
          )}
          {SHOW_NUMERIC_SENTIMENT && (
            <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">Sentiment Trendline</h2>
                <TrendingUp className="h-5 w-5 text-emerald-500" />
              </div>
              <p className="mt-2 text-sm text-slate-600">Emotional trajectory across your journey.</p>
              <div className="mt-4 space-y-3">
                {sentimentTrend.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
                    We’ll chart sentiment once MoMs capture more consistent themes.
                  </div>
                ) : (
                  sentimentTrend.map((entry) => (
                    <div key={entry.date} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{entry.date}</p>
                        <p className="text-xs text-slate-500">MoM tone for this month</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${sentimentColor(entry.score)}`}>
                        {entry.score.toFixed(2)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </section>
          )}

        </div>
      </div>
    </AppLayout>
  );
};

export default InsightsPage;
